import { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react';
import styled from 'styled-components';
import { HeadlessButton } from '../button';
import { Icon, IconDictionary } from '../icon';
import { theme } from './action_card.theme';

type ActionCardTrailingIcon = keyof Pick<IconDictionary, 'linkOut' | 'arrowRight'>;

interface ActionCardContentProps {
	children: ReactNode;
	variant?: 'default' | 'chunky';
	icon?: keyof IconDictionary | JSX.Element;
	iconBackground?: string;
	trailingIcon?: ActionCardTrailingIcon | null;
}

interface ActionCardRootProps extends ButtonHTMLAttributes<HTMLButtonElement> {
	children: ReactNode;
	variant?: 'default' | 'chunky';
	icon?: keyof IconDictionary | JSX.Element;
	iconBackground?: string;
	trailingIcon?: ActionCardTrailingIcon | null;
	as?: 'button' | 'span';
}

interface ActionCardLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
	children: ReactNode;
	href: string;
	variant?: 'default' | 'chunky';
	icon?: keyof IconDictionary | JSX.Element;
	iconBackground?: string;
	trailingIcon?: ActionCardTrailingIcon | null;
}

export function ActionCardRoot({
	children,
	variant = 'default',
	icon,
	trailingIcon = 'arrowRight',
	disabled,
	onClick,
	iconBackground,
	as = 'button',
	...rest
}: ActionCardRootProps) {
	function handleClick(event: React.MouseEvent<HTMLButtonElement>) {
		if (disabled || as === 'span') return;

		onClick?.(event);
	}

	return (
		<StyledActionCardRoot
			data-variant={variant}
			disabled={disabled}
			as={as === 'span' ? 'span' : undefined}
			aria-disabled={disabled ? 'true' : undefined}
			onClick={handleClick}
			{...rest}
		>
			<ActionCardContent icon={icon} trailingIcon={trailingIcon} iconBackground={iconBackground}>
				{children}
			</ActionCardContent>
		</StyledActionCardRoot>
	);
}

export function ActionCardLink({
	children,
	variant = 'default',
	icon,
	trailingIcon = 'arrowRight',
	href,
	iconBackground,
	...rest
}: ActionCardLinkProps) {
	return (
		<StyledActionCardRoot as='a' data-variant={variant} href={href} {...rest}>
			<ActionCardContent icon={icon} trailingIcon={trailingIcon} iconBackground={iconBackground}>
				{children}
			</ActionCardContent>
		</StyledActionCardRoot>
	);
}

function ActionCardContent({ children, icon, trailingIcon, iconBackground }: ActionCardContentProps) {
	return (
		<>
			{icon ? (
				<span className='c-action-card__icon' style={iconBackground ? { background: `${iconBackground}` } : undefined}>
					{typeof icon === 'string' ? <Icon type={icon} /> : icon}
				</span>
			) : null}

			<span className='c-action-card__content'>{children}</span>
			{trailingIcon ? (
				<span className='c-action-card__arrow'>
					<Icon type={trailingIcon} />
				</span>
			) : null}
		</>
	);
}

const StyledActionCardRoot = styled(HeadlessButton)`
	--icon-vertical-padding: ${theme.getValue('iconVerticalPadding')};
	--icon-horizontal-padding: ${theme.getValue('iconHorizontalPadding')};
	--content-padding: ${theme.getValue('contentPadding')};
	--content-gap: ${theme.getValue('contentGap')};
	--title-font: ${theme.getValue('titleFont')};
	--meta-font: ${theme.getValue('metaFont')};

	all: unset;
	background: #fff;
	border-radius: ${theme.getValue('baseBorderRadius')};
	border: 1px solid ${theme.getValue('baseBorderColor')};
	box-shadow: ${theme.getValue('baseBoxShadow')};
	cursor: default;
	transition: box-shadow 100ms ease-in;
	display: flex;
	flex: 1 1 auto;
	align-items: center;
	min-height: ${theme.getValue('baseMinHeight')};

	&:hover {
		box-shadow: ${theme.getValue('hoverBoxShadow')};
	}

	&:active {
		box-shadow: none;
		transition: box-shadow 50ms ease-in;
	}

	&:focus-visible {
		outline: 2px solid ${theme.getValue('baseFocusRingColor')};
	}

	.c-action-card__icon {
		display: flex;
		align-items: center;
		padding: var(--icon-vertical-padding) var(--icon-horizontal-padding);
		background: ${theme.getValue('iconBackground')};
		border-top-left-radius: ${theme.getValue('baseBorderRadius')};
		border-bottom-left-radius: ${theme.getValue('baseBorderRadius')};
		color: ${theme.getValue('iconColor')};
		align-self: stretch;
		flex: 0 0 auto;
		position: relative;
		overflow: hidden;
		width: calc(${theme.getValue('iconSize')} + (var(--icon-horizontal-padding)) * 2);

		& > svg {
			max-width: ${theme.getValue('iconSize')};
			max-height: ${theme.getValue('iconSize')};
			width: ${theme.getValue('iconSize')};
			height: ${theme.getValue('iconSize')};
		}
	}

	.c-action-card__content {
		display: flex;
		flex: 1 1 auto;
		flex-direction: column;
		gap: var(--content-gap);
		padding: var(--content-padding);
		overflow-wrap: break-word;
		word-break: break-word;
	}

	.c-action-card__title {
		font: var(--title-font);
		color: ${theme.getValue('titleFontColor')};
	}

	.c-action-card__meta {
		font: var(--meta-font);
		color: ${theme.getValue('metaFontColor')};
	}

	.c-action-card__arrow {
		flex: 0 0 auto;
		margin-left: auto;
		padding: ${theme.getValue('arrowPadding')};
		padding-left: 0;
		color: ${theme.getValue('arrowColor')};
	}

	&[aria-disabled='true'],
	:disabled {
		opacity: 0.7;
		cursor: not-allowed;
		box-shadow: none;
	}

	&[data-variant='chunky'] {
		--icon-vertical-padding: ${theme.getValue('chunkyIconVerticalPadding')};
		--icon-horizontal-padding: ${theme.getValue('chunkyIconHorizontalPadding')};
		--content-padding: ${theme.getValue('chunkyContentPadding')};
		--content-gap: ${theme.getValue('chunkyContentGap')};
		--title-font: ${theme.getValue('chunkyTitleFont')};
		--meta-font: ${theme.getValue('chunkyMetaFont')};

		align-items: flex-start;
		min-height: ${theme.getValue('chunkyMinHeight')};

		.c-action-card__icon {
			align-items: flex-start;
		}
		.c-action-card__arrow {
			align-self: flex-end;
		}
	}
`;
